<script lang="ts">
import * as d3 from "d3";

export interface ParetoPoint {
  id: number;
  x: number;
  y: number;
  isParetoOptimum?: boolean;
  hover?: boolean;
  shape?: d3.SymbolType;
}

export interface ParetoOptimizationDirections {
  x: boolean;
  y: boolean;
}

export function addParetoOptimality(
  points: ParetoPoint[],
  directions: ParetoOptimizationDirections
): ParetoPoint[] {
  // Create a copy of the data to avoid mutation
  const result = [...points];

  // For each point, check if it's dominated by any other point
  for (let i = 0; i < result.length; i++) {
    const currentPoint = result[i];
    let isDominated = false;

    for (let j = 0; j < result.length; j++) {
      if (i === j) continue; // Skip comparing point with itself

      const otherPoint = result[j];

      // Check if otherPoint dominates currentPoint
      if (dominates(otherPoint, currentPoint, directions)) {
        isDominated = true;
        break;
      }
    }

    currentPoint.isParetoOptimum = !isDominated;
  }

  return result;
}

function dominates(
  pointA: ParetoPoint,
  pointB: ParetoPoint,
  directions: ParetoOptimizationDirections
): boolean {
  // Point A dominates point B if:
  // 1. A is at least as good as B in all objectives
  // 2. A is strictly better than B in at least one objective

  let atLeastAsGoodInAll = true;
  let strictlyBetterInAtLeast = false;

  // Check x dimension
  const xComparison = compareValues(pointA.x, pointB.x, directions.x);
  if (xComparison < 0) {
    atLeastAsGoodInAll = false;
  } else if (xComparison > 0) {
    strictlyBetterInAtLeast = true;
  }

  // Check y dimension
  const yComparison = compareValues(pointA.y, pointB.y, directions.y);
  if (yComparison < 0) {
    atLeastAsGoodInAll = false;
  } else if (yComparison > 0) {
    strictlyBetterInAtLeast = true;
  }

  return atLeastAsGoodInAll && strictlyBetterInAtLeast;
}

function compareValues(a: number, b: number, greaterIsBetter: boolean): number {
  // Returns:
  //  1 if a is better than b
  //  0 if a equals b
  // -1 if a is worse than b

  if (a === b) return 0;

  if (greaterIsBetter) {
    return a > b ? 1 : -1;
  } else {
    return a < b ? 1 : -1;
  }
}
</script>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from "vue";

import { generateRandomId, intervalMaxMinRatio } from "@/services/utils";

const {
  points,
  directions,
  pointSize = 32,
  dashLength = 4,
  animationDuration = 1500,
} = defineProps<{
  points: ParetoPoint[];
  directions: ParetoOptimizationDirections;
  pointSize?: number;
  dashLength?: number;
  animationDuration?: number;
}>();
const emit = defineEmits<{
  mouseOverPoint: [id: number, x: number, y: number];
  mouseOutPoint: [];
  clickPoint: [id: number, x: number, y: number];
}>();
defineExpose({
  getCoordinates,
});

const plot = useTemplateRef<SVGElement>("plot");
const gradientRotation = ref(0);
const gradientTransformOrigin = ref({ x: 0, y: 0 });
const gradientId = generateRandomId();

const margin = { top: 0, right: 0, bottom: 30, left: 40 };

function getBestScale(samples: number[]) {
  const r = intervalMaxMinRatio(samples);
  return r > 5 ? d3.scaleSymlog() : d3.scaleLinear();
}

function getCoordinates(pointId: number) {
  const svg = d3.select(plot.value);
  const p = svg.select(`[point-id="${pointId}"]`);
  if (p) {
    return {
      x: parseFloat(p.attr("x")),
      y: parseFloat(p.attr("y")),
    };
  } else {
    return {
      x: -1,
      y: -1,
    };
  }
}

function updatePlot(animate: boolean) {
  if (plot.value) {
    const width = plot.value.clientWidth;
    const height = plot.value.clientHeight;
    const svg = d3.select(plot.value);
    const axesG = svg.select<SVGGElement>(".axes");
    const axisX = axesG.select<SVGGElement>(".x");
    const axisY = axesG.select<SVGGElement>(".y");
    const pointsG = svg.select<SVGGElement>(".points");
    const frontLinePath = svg.select<SVGPathElement>(".front .line path");
    const dominatedRegionPath = svg.select<SVGPathElement>(".front .dominated-region path");

    // axes
    const x = points.map((p) => p.x);
    const [xMin, xMax] = d3.extent(x) as [number, number];
    const xScale = getBestScale(x)
      .range([margin.left + pointSize, width - margin.right - pointSize])
      .domain([xMin, xMax]);
    axisX
      .attr("transform", `translate(0, ${height - margin.bottom})`)
      .transition()
      .duration(200)
      .call(d3.axisBottom(xScale).tickSize(-height).tickFormat(d3.format(".2")));

    const y = points.map((p) => p.y);
    const [yMin, yMax] = d3.extent(y) as [number, number];
    const yScale = getBestScale(y)
      .range([height - margin.bottom - pointSize, margin.top + pointSize])
      .domain([yMin, yMax]);
    axisY
      .attr("transform", `translate(${margin.left}, 0)`)
      .transition()
      .duration(200)
      .call(d3.axisLeft(yScale).tickSize(-width));

    // points
    const paretoPoints = addParetoOptimality(points, directions);
    const pointsSelection = pointsG
      .selectAll<SVGPathElement, ParetoPoint>(".point")
      .data(paretoPoints, (d) => d.id);

    const enter = pointsSelection.enter().append("path").attr("class", "point");

    const merged = enter.merge(pointsSelection);
    merged
      .transition()
      .duration(200)
      .delay((_, i) => i * 0.2)
      .attr("d", (d) => d3.symbol(d.shape ?? d3.symbolSquare2, pointSize)())
      .attr("point-id", (d) => d.id)
      .attr("x", (d) => xScale(d.x))
      .attr("y", (d) => yScale(d.y))
      .attr("transform", (d) => `translate(${xScale(d.x)}, ${yScale(d.y)})`);

    merged
      .on("mouseover", (_, datum) => {
        const { x, y } = getCoordinates(datum.id);
        emit("mouseOverPoint", datum.id, x, y);
      })
      .on("mouseout", () => {
        emit("mouseOutPoint");
      })
      .on("click", (_, datum) => {
        const { x, y } = getCoordinates(datum.id);
        emit("clickPoint", datum.id, x, y);
      })
      .classed("pareto-optimum", (p) => p.isParetoOptimum ?? false);

    pointsSelection.exit().transition().duration(300).remove();

    // front line
    const front = paretoPoints
      .filter((p) => p.isParetoOptimum)
      .sort((a, b) => a.x - b.x)
      .map((p) => ({ x: p.x, y: p.y }));
    const [minX, maxX] = xScale.domain();
    const [minY, maxY] = yScale.domain();
    const first = { x: 0, y: 0 };
    const last = { x: 0, y: 0 };

    // prepare endpoints
    if (directions.x && directions.y) {
      first.x = minX;
      first.y = maxY;
      last.x = maxX;
      last.y = minY;
    }
    if (!directions.x && !directions.y) {
      first.x = minX;
      first.y = maxY;
      last.x = maxX;
      last.y = minY;
    }
    if (!directions.x && directions.y) {
      first.x = minX;
      first.y = minY;
      last.x = maxX;
      last.y = maxY;
    }
    if (directions.x && !directions.y) {
      first.x = minX;
      first.y = minY;
      last.x = maxX;
      last.y = maxY;
    }
    front.unshift(first);
    front.push(last);

    // draw
    const frontLineGenerator = d3
      .line<{ x: number; y: number }>()
      .x((d) => xScale(d.x))
      .y((d) => yScale(d.y))
      .curve(directions.x ? d3.curveStepBefore : d3.curveStepAfter);
    frontLinePath.attr("d", frontLineGenerator(front));

    if (animate) {
      const totalLength = frontLinePath.node()?.getTotalLength() ?? 0;
      const dashCount = Math.ceil(totalLength / dashLength);
      const dashArray = Array.from({ length: dashCount }, () => dashLength);
      dashArray.push(0, totalLength);
      frontLinePath
        .attr("stroke-dasharray", dashArray.join(", "))
        .attr("stroke-dashoffset", totalLength)
        .transition()
        .duration(animationDuration)
        .ease(d3.easeCubicInOut)
        .attr("stroke-dashoffset", 0);
    } else {
      frontLinePath
        .attr("stroke-dasharray", `${dashLength}, ${dashLength}`)
        .attr("stroke-dashoffset", 0);
    }

    // dominated region
    const areaGenerator = d3
      .area<{ x: number; y: number }>()
      .x((d) => xScale(d.x))
      .curve(directions.x ? d3.curveStepBefore : d3.curveStepAfter);
    if (directions.y) {
      areaGenerator.y0(yScale(yMin));
      areaGenerator.y1((d) => yScale(d.y));
    } else {
      areaGenerator.y0((d) => yScale(d.y));
      areaGenerator.y1(yScale(yMax));
    }
    if (directions.x && directions.y) {
      gradientRotation.value = 115;
    }
    if (!directions.x && !directions.y) {
      gradientRotation.value = 295;
    }
    if (!directions.x && directions.y) {
      gradientRotation.value = 65;
    }
    if (directions.x && !directions.y) {
      gradientRotation.value = 245;
    }
    dominatedRegionPath.attr("d", areaGenerator(front));
    gradientTransformOrigin.value = { x: width / 2 + margin.left, y: height / 2 - margin.bottom };
    if (animate) {
      const gradient = svg.select<SVGLinearGradientElement>(`linearGradient`);
      gradient
        .select(".dominated-region-gradient-stop1")
        .attr("offset", "0%")
        .transition()
        .duration(animationDuration)
        .attr("offset", "20%");
      gradient
        .select(".dominated-region-gradient-stop2")
        .attr("offset", "0%")
        .transition()
        .duration(animationDuration)
        .attr("offset", "57%");
    }
  }
}

const resizeObserver = new ResizeObserver(() => {
  updatePlot(false);
});

watch(
  () => points,
  (newPoints, oldPoints) => {
    const newX = newPoints.map((p) => p.x);
    const oldX = oldPoints.map((p) => p.x);
    const newY = newPoints.map((p) => p.y);
    const oldY = oldPoints.map((p) => p.y);
    const xScaleHasChanged = !newX.every((v, i) => v === oldX[i]);
    const yScaleHasChanged = !newY.every((v, i) => v === oldY[i]);
    const shouldAnimate = xScaleHasChanged || yScaleHasChanged;
    updatePlot(shouldAnimate);
  }
);

onMounted(async () => {
  // be sure that container has its full size already
  await nextTick();
  updatePlot(true);
  if (plot.value) {
    resizeObserver.observe(plot.value);
  }
});

onBeforeUnmount(() => {
  if (plot.value) {
    resizeObserver.unobserve(plot.value);
  }
});
</script>

<template>
  <svg ref="plot" class="pareto-diagram">
    <defs>
      <linearGradient
        :id="gradientId"
        gradientUnits="userSpaceOnUse"
        :gradientTransform="`rotate(${gradientRotation}, ${gradientTransformOrigin.x}, ${gradientTransformOrigin.y})`"
      >
        <stop class="dominated-region-gradient-stop1" offset="20%" />
        <stop class="dominated-region-gradient-stop2" offset="57%" />
      </linearGradient>
    </defs>
    <g class="axes">
      <g class="x"></g>
      <g class="y"></g>
    </g>
    <g class="front">
      <g class="line"><path /></g>
      <g class="dominated-region"><path /></g>
    </g>
    <g class="points"></g>
  </svg>
</template>

<style>
.pareto-diagram {
  width: 100%;
  height: 100%;

  text {
    font-family: GeistMono, monospace;
  }

  path.point {
    width: v-bind('pointSize + "px"');
    height: v-bind('pointSize + "px"');
    cursor: pointer;
    fill: var(--color-background-tertiary);
    stroke: #5e5f5f;
    stroke-width: 2;
    transition:
      fill var(--animation-duration) var(--animation-easing),
      stroke var(--animation-duration) var(--animation-easing);

    &.pareto-optimum {
      fill: hwb(31deg 83% 1%);
      stroke: hwb(31deg 21% 3%);
    }
  }

  g.axes {
    path.domain {
      stroke: transparent;
      stroke-width: 0;
    }

    .tick line {
      opacity: 0.2;
      stroke: var(--color-text-secondary);
      stroke-width: 1;
    }

    .tick text {
      fill: var(--color-text-secondary);
    }
  }

  g.front {
    .line {
      path {
        fill: none;
        stroke: #1e22aa;
        stroke-width: 1;
      }
    }

    .dominated-region {
      fill: v-bind('"url(#" + gradientId + ")"');
      stroke: none;
    }
  }

  .dominated-region-gradient-stop1 {
    stop-color: rgb(48 67 240 / 20%);
  }

  .dominated-region-gradient-stop2 {
    stop-color: rgb(48 67 240 / 0%);
  }
}
</style>
